/**
 * useActiveCauldron — single reactive resolver for the active cauldron.
 *
 * Resolution priority:
 *   1. `route.meta.cauldron` — explicit tag from `src/router/index.js`
 *      (added in the cauldron-segregation refactor). This is the
 *      canonical source: every top-level route ships its cauldron in
 *      `meta`, and Vue Router merges parent meta into children so
 *      sub-pages inherit automatically.
 *   2. `routePrefixes` match — fallback for routes that haven't been
 *      tagged yet (e.g. a new tool route that landed before someone
 *      added the meta). Lets the rail keep lighting up correctly
 *      while a tag is being added.
 *   3. `null` — no cauldron. The rail's Home icon stays the only lit
 *      affordance; the contextual sidebar renders nothing. Used by
 *      Home, /profile, /subscriptions, /api-docs-auth/*, and any
 *      auth/error/embedded views.
 *
 * Consumed by `CauldronRail.vue`, the three cauldron sidebars, and
 * `StandardToolPage.vue` (eyebrow fallback).
 */
import { CAULDRONS, getCauldronById, getCauldronByPath } from '@/config/domain/cauldrons';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export function useActiveCauldron() {
  // `useRoute()` is undefined in some component tests that don't install a
  // router. Returning an empty object keeps the composable safe to mount
  // in those tests without forcing every spec to set up a router stub.
  const route = useRoute() || {};

  const activeCauldron = computed(() => {
    const meta = route.meta || {};
    if (meta.cauldron) {
      const fromMeta = getCauldronById(meta.cauldron);
      if (fromMeta) return fromMeta;
    }
    return getCauldronByPath(route.path);
  });

  const activeCauldronId = computed(() => activeCauldron.value?.id ?? null);

  function isActive(id) {
    return activeCauldronId.value === id;
  }

  return {
    cauldrons: CAULDRONS,
    activeCauldron,
    activeCauldronId,
    isActive,
  };
}
